import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  MapPin, Loader2, Users, ShieldCheck, Maximize2,
} from "lucide-react";
import { loadGoogleMaps } from "../lib/googleMaps";

/**
 * SatellitePriceLoader — iter241 (2026-08-22)
 *
 * Owner directive:
 *   Replace the 6-second price-calculation animation with a "Property
 *   Verified" preview that looks like a real-estate satellite card.
 *   Uses the Google Maps JavaScript API (which is already enabled for
 *   Places Autocomplete) to render a real satellite tile centered on
 *   the customer's exact property. All map interactions are disabled so
 *   the map behaves like a still image.
 *
 * Props:
 *   lat, lng         — optional; if missing the address is geocoded
 *   address          — required for the "Service Address" strip
 *   durationMs       — total loader duration (default 6000 = 6s)
 *   onComplete       — called when the timer finishes
 */

const pubApi = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: false,
});

export default function SatellitePriceLoader({
  lat, lng, address, durationMs = 6000, onComplete,
}) {
  const [progress, setProgress] = useState(0);
  const [nearby, setNearby] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);

  // Progress ticker
  useEffect(() => {
    const startedAt = Date.now();
    const t = setInterval(() => {
      const pct = Math.min(1, (Date.now() - startedAt) / durationMs);
      setProgress(pct);
      if (pct >= 1) {
        clearInterval(t);
        if (onComplete) onComplete();
      }
    }, 100);
    return () => clearInterval(t);
  }, []);

  // Social proof lookup
  useEffect(() => {
    if (typeof lat !== "number" || typeof lng !== "number") return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await pubApi.get("/community/nearby-stats", {
          params: { lat, lng, radius_miles: 3 },
        });
        if (!cancelled && data?.ok) setNearby(data);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [lat, lng]);

  // Render the real Google Maps satellite tile. Interactions disabled so
  // it feels like a still image.
  useEffect(() => {
    let cancelled = false;
    const hasCoords = typeof lat === "number" && typeof lng === "number"
                      && !Number.isNaN(lat) && !Number.isNaN(lng);
    if (!hasCoords && !address) return;

    (async () => {
      try {
        const google = await loadGoogleMaps();
        if (cancelled || !mapContainerRef.current) return;

        // Resolve coordinates
        let center;
        if (hasCoords) {
          center = new google.maps.LatLng(lat, lng);
        } else {
          const geocoder = new google.maps.Geocoder();
          const result = await new Promise((resolve, reject) => {
            geocoder.geocode({ address }, (res, status) => {
              if (status === "OK" && res && res[0]) resolve(res[0]);
              else reject(new Error(`geocode ${status}`));
            });
          });
          center = result.geometry.location;
        }
        if (cancelled) return;

        const map = new google.maps.Map(mapContainerRef.current, {
          center,
          zoom: 20,
          mapTypeId: google.maps.MapTypeId.SATELLITE,
          tilt: 0,
          disableDefaultUI: true,
          gestureHandling: "none",
          keyboardShortcuts: false,
          draggable: false,
          clickableIcons: false,
          disableDoubleClickZoom: true,
          scrollwheel: false,
        });
        mapInstanceRef.current = map;
        // Force satellite (no labels overlay for a cleaner property look)
        map.setMapTypeId(google.maps.MapTypeId.SATELLITE);
        // Wait for tiles then flip visibility flag so overlays fade in
        google.maps.event.addListenerOnce(map, "idle", () => {
          if (!cancelled) setMapReady(true);
        });
        // Failsafe: mark ready after 1.2s even if idle didn't fire
        setTimeout(() => { if (!cancelled) setMapReady(true); }, 1200);
      } catch {
        // Silent — component still shows the badges + address strip.
        if (!cancelled) setMapReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [lat, lng, address]);

  // Keep map centered/sized when toggling fullscreen
  useEffect(() => {
    if (mapInstanceRef.current && window.google?.maps) {
      window.google.maps.event.trigger(mapInstanceRef.current, "resize");
    }
  }, [fullscreen]);

  const neighborLine = useMemo(() => {
    const n = nearby?.nearby_customers || 0;
    if (n >= 10) return `Join ${n}+ neighbors near you who trust JDSN Lawn Care.`;
    if (n > 0)   return `Join ${n} neighbor${n === 1 ? "" : "s"} near you who trust JDSN Lawn Care.`;
    return "Join 10+ neighbors in Jacksonville who trust JDSN Lawn Care.";
  }, [nearby]);

  const openFullscreen = () => {
    setFullscreen(true);
    try {
      const el = mapContainerRef.current?.closest("[data-testid='satellite-property-card']");
      if (el && document.fullscreenEnabled) el.requestFullscreen?.();
    } catch { /* fullscreen API unavailable */ }
  };
  useEffect(() => {
    const onFsChange = () => {
      if (!document.fullscreenElement) setFullscreen(false);
    };
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gradient-to-br from-emerald-950 via-emerald-900 to-emerald-950 p-4 sm:p-8"
          data-testid="satellite-loader">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-4">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] font-extrabold text-emerald-300"
                data-testid="satellite-loader-eyebrow">
            <ShieldCheck className="w-3 h-3" /> Satellite precision · analyzing property
          </div>
          <h2 className="mt-2 text-2xl sm:text-3xl font-extrabold text-white tracking-tight font-[Outfit] leading-tight"
               data-testid="satellite-loader-headline">
            We&apos;re sizing your lawn with satellite precision
          </h2>
        </div>

        <div className="rounded-3xl overflow-hidden bg-white shadow-[0_30px_80px_rgba(6,78,59,0.55)] border border-emerald-500/40"
              data-testid="satellite-property-card">
          <div className="relative w-full aspect-[4/3] bg-emerald-950"
                data-testid="satellite-loader-image">
            {/* Real satellite map */}
            <div ref={mapContainerRef}
                  className={`absolute inset-0 w-full h-full transition-opacity duration-700 ${mapReady ? "opacity-100" : "opacity-0"}`}
                  aria-label={address ? `Satellite view of ${address}` : "Satellite view of your property"} />

            {/* Loading skeleton while tiles load */}
            {!mapReady && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-emerald-200 gap-2 animate-pulse"
                    data-testid="satellite-loader-map-loading">
                <MapPin className="w-8 h-8" />
                <div className="text-[11px] uppercase tracking-[0.2em] font-extrabold">Loading satellite tiles…</div>
              </div>
            )}

            {/* Vignette so labels sit clean over the imagery */}
            <div className="absolute inset-0 pointer-events-none"
                  style={{ background: "radial-gradient(ellipse at center, rgba(0,0,0,0) 40%, rgba(0,0,0,0.35) 100%)" }} />

            {/* Lawn area overlay */}
            <div className="absolute pointer-events-none rounded-md ring-2 ring-emerald-300"
                  data-testid="satellite-loader-lawn-overlay"
                  style={{
                    left: "16%", right: "20%",
                    top: "34%", bottom: "18%",
                    background: "linear-gradient(135deg, rgba(52,211,153,0.32), rgba(16,185,129,0.22))",
                    boxShadow: "0 0 0 1px rgba(6,95,70,0.4) inset",
                  }} />
            <div className="absolute pointer-events-none"
                  style={{ left: "20%", top: "36%" }}
                  data-testid="satellite-loader-lawn-label">
              <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500 text-white text-[10px] font-extrabold uppercase tracking-[0.14em] shadow-md">
                <span className="w-1.5 h-1.5 rounded-full bg-white" />
                Lawn area
              </div>
            </div>

            {/* Property marker */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none flex flex-col items-center"
                  data-testid="satellite-loader-you-pin">
              <div className="relative">
                <div className="w-6 h-6 rounded-full bg-red-500 ring-4 ring-white shadow-[0_6px_20px_rgba(220,38,38,0.55)] animate-pulse" />
                <div className="absolute inset-0 rounded-full bg-red-500/40 animate-ping" />
              </div>
              <div className="mt-1.5 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white text-gray-900 text-[10px] font-extrabold uppercase tracking-[0.14em] shadow-md"
                    data-testid="satellite-loader-you-label">
                <MapPin className="w-3 h-3 text-red-500" />
                Your property
              </div>
            </div>

            {/* PROPERTY VERIFIED badge */}
            <div className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500 text-white text-[11px] font-extrabold uppercase tracking-[0.16em] shadow-lg"
                  data-testid="property-verified-badge">
              <ShieldCheck className="w-3.5 h-3.5" />
              Property Verified
            </div>

            {/* FULLSCREEN button */}
            <button
              type="button"
              onClick={openFullscreen}
              className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/95 hover:bg-white text-gray-900 text-[11px] font-extrabold uppercase tracking-[0.16em] shadow-lg backdrop-blur-sm transition active:scale-95"
              data-testid="satellite-fullscreen-btn"
              aria-label="View satellite image full screen"
            >
              <Maximize2 className="w-3.5 h-3.5" />
              Fullscreen
            </button>

            {/* Progress bar */}
            <div className="absolute inset-x-0 bottom-0 h-1 bg-emerald-950/40 overflow-hidden">
              <div className="h-full bg-emerald-400 transition-[width] duration-100 ease-linear"
                    style={{ width: `${Math.round(progress * 100)}%` }}
                    data-testid="satellite-loader-progress-bar" />
            </div>
          </div>

          {/* Service Address strip */}
          <div className="px-5 py-4 sm:px-6 sm:py-5 bg-white flex items-start gap-3"
                data-testid="satellite-loader-address-block">
            <div className="mt-0.5 shrink-0 w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
              <MapPin className="w-4 h-4 text-emerald-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-emerald-700"
                    data-testid="satellite-loader-service-address-label">
                Service Address
              </div>
              <div className="mt-0.5 text-sm sm:text-base font-extrabold text-gray-900 truncate"
                    data-testid="satellite-loader-address">
                {address || "Locating your property…"}
              </div>
              <div className="mt-1 text-[11px] font-semibold text-gray-500">
                <Loader2 className="w-3 h-3 inline animate-spin mr-1 text-emerald-600" />
                Calculating your price · {Math.round(progress * 100)}%
              </div>
            </div>
          </div>
        </div>

        <div className="mt-5 mx-auto max-w-md rounded-2xl bg-emerald-800/50 border border-emerald-500/30 px-4 py-3"
              data-testid="satellite-loader-social-proof">
          <div className="flex items-center justify-center gap-2 text-emerald-100 text-sm font-extrabold">
            <Users className="w-4 h-4 text-emerald-300" />
            You&apos;re not alone.
          </div>
          <div className="mt-0.5 text-[12px] text-emerald-200/90 font-semibold text-center"
                data-testid="satellite-loader-neighbors-text">
            {neighborLine}
          </div>
        </div>
      </div>
    </div>
  );
}
